// src/features/spotting/pages/PlateAggregation.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { db } from '../../../firebase';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { ArrowLeft, MapPin, Calendar, TrendingUp, Car, MessageSquare, FileText, X, ExternalLink, Share2 } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { getRelativeTime, getFullDate } from '../../../utils/helpers';

// ========================================
// MODAL RAPORTU
// ========================================
const ReportModal = ({ plate, stats, spots, onClose }) => {
  const allImages = spots.flatMap(s => s.images || (s.imageUrl ? [s.imageUrl] : []));
  const uniqueImages = [...new Set(allImages)];
  const mileageSpots = spots.filter(s => s.mileage);
  const carVerticalUrl = `https://www.carvertical.com/pl/vehicle-history-report?vin=&plate=${plate.toUpperCase()}&country=PL`;

  const handleShare = async () => {
    const url = `https://motoradar24.pl/tablica/${plate.toLowerCase()}`;
    if (navigator.share) {
      await navigator.share({ title: `Raport pojazdu ${plate.toUpperCase()}`, url });
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-5 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <FileText size={18} className="text-blue-400"/>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Raport Pojazdu</span>
            </div>
            <h2 className="text-2xl font-black">{plate.toUpperCase()}</h2>
            {stats.brand && <p className="text-slate-300 text-sm">{stats.brand}</p>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors">
            <X size={22}/>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 min-h-0 p-5 space-y-4">

          {/* Statystyki */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-center">
              <div className="text-2xl font-black text-red-600">{stats.totalSpots}</div>
              <div className="text-xs text-red-500 font-bold">Zgłoszeń</div>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-center">
              <div className="text-2xl font-black text-blue-600">{stats.totalUpvotes}</div>
              <div className="text-xs text-blue-500 font-bold">Głosów</div>
            </div>
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-center">
              <div className="text-2xl font-black text-slate-700">{stats.totalComments}</div>
              <div className="text-xs text-slate-500 font-bold">Komentarzy</div>
            </div>
          </div>

          {/* Miasta */}
          {stats.cities.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={15} className="text-slate-500"/>
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Widziany w miastach</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {stats.cities.map((city, i) => (
                  <span key={i} className="px-3 py-1 bg-white border border-slate-200 text-slate-700 rounded-full text-xs font-bold">
                    {city}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Historia dat */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-xl p-3">
              <div className="text-xs text-slate-500 font-bold uppercase mb-1">Pierwsze zgłoszenie</div>
              <div className="text-sm font-bold text-slate-800">
                {stats.firstSeen ? getRelativeTime(stats.firstSeen) : '-'}
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <div className="text-xs text-slate-500 font-bold uppercase mb-1">Ostatnie zgłoszenie</div>
              <div className="text-sm font-bold text-slate-800">
                {stats.lastSeen ? getRelativeTime(stats.lastSeen) : '-'}
              </div>
            </div>
          </div>

          {/* Stan licznika */}
          {mileageSpots.length > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
              <div className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">🔢 Historia licznika</div>
              <div className="space-y-1">
                {mileageSpots.map((s, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="font-bold text-slate-800">{s.mileage.toLocaleString()} km</span>
                    <span className="text-slate-500">{getRelativeTime(s.createdAt)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Historia incydentów */}
          <div>
            <div className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">📋 Historia incydentów</div>
            <div className="space-y-2">
              {spots.map((spot, i) => (
                <Link
                  key={i}
                  to={`/pojazd/${spot.plateSlug || spot.id}`}
                  onClick={onClose}
                  className="flex gap-3 bg-slate-50 rounded-xl p-3 hover:bg-slate-100 transition-colors border border-slate-100"
                >
                  {(spot.images?.[0] || spot.imageUrl) && (
                    <img
                      src={spot.images?.[0] || spot.imageUrl}
                      alt="spot"
                      className="w-14 h-14 object-cover rounded-lg flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-slate-500">{getRelativeTime(spot.createdAt)}</span>
                      {spot.locationName && (
                        <span className="text-xs text-slate-400">• {spot.locationName}</span>
                      )}
                      {spot.flair && spot.flair !== 'chamstwo' && (
                        <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-bold">
                          {spot.flair}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 line-clamp-2">{spot.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Wszystkie zdjęcia */}
          {uniqueImages.length > 0 && (
            <div>
              <div className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">
                📸 Wszystkie zdjęcia ({uniqueImages.length})
              </div>
              <div className="grid grid-cols-3 gap-2">
                {uniqueImages.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`zdjęcie ${i+1}`}
                    className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                  />
                ))}
              </div>
            </div>
          )}

          {/* CarVertical affiliate */}
          <a
            href={carVerticalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl p-4 hover:from-blue-700 hover:to-blue-800 transition-all"
          >
            <div>
              <div className="font-black text-sm">🔍 Sprawdź historię techniczną</div>
              <div className="text-blue-200 text-xs">CarVertical — VIN, przeglądy, szkody, kradzieże</div>
            </div>
            <ExternalLink size={18} className="flex-shrink-0"/>
          </a>

          {/* Udostępnij */}
          <button
            onClick={handleShare}
            className="w-full flex items-center justify-center gap-2 border-2 border-slate-200 text-slate-700 rounded-xl p-3 font-bold text-sm hover:bg-slate-50 transition-colors"
          >
            <Share2 size={16}/>
            Udostępnij raport
          </button>

        </div>
      </div>
    </div>
  );
};

// ========================================
// GŁÓWNY KOMPONENT
// ========================================
export const PlateAggregation = ({ showToast, setReportId, setZoomImg }) => {
  const { plate } = useParams();
  const navigate = useNavigate();
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [stats, setStats] = useState({
    totalSpots: 0,
    totalUpvotes: 0,
    totalComments: 0,
    cities: [],
    firstSeen: null,
    lastSeen: null,
    brand: null
  });

  useEffect(() => {
    const fetchPlateSpots = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, 'sightings'),
          where('plateSlug', '==', plate.toLowerCase()),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);
        const spotsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setSpots(spotsData);

        if (spotsData.length > 0) {
          const cities = [...new Set(spotsData.map(s => s.locationName || s.city).filter(Boolean))];
          const totalUpvotes = spotsData.reduce((sum, s) => sum + (s.upvotes || 0), 0);
          const totalComments = spotsData.reduce((sum, s) => sum + (s.commentCount || 0), 0);
          const firstSeen = spotsData[spotsData.length - 1].createdAt;
          const lastSeen = spotsData[0].createdAt;
          const brand = spotsData[0].brand;

          setStats({ totalSpots: spotsData.length, totalUpvotes, totalComments, cities, firstSeen, lastSeen, brand });
        }
      } catch (error) {
        console.error('Error fetching plate spots:', error);
        showToast?.('Błąd ładowania', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPlateSpots();
  }, [plate, showToast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (spots.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 text-sm hover:text-blue-600 transition-colors mb-4">
          <ArrowLeft size={18}/> Powrót
        </button>
        <div className="bg-white rounded-2xl border p-8 text-center">
          <Car size={48} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Nie znaleziono spotów</h2>
          <p className="text-gray-600">Pojazd {plate.toUpperCase()} nie został jeszcze zgłoszony.</p>
        </div>
      </div>
    );
  }

  const mapMarkers = spots
    .filter(s => s.location?.lat && s.location?.lng)
    .map(s => ({
      lat: s.location.lat,
      lng: s.location.lng,
      label: s.locationName || 'Nieznana lokalizacja',
      id: s.id,
      createdAt: s.createdAt
    }));

  const centerLat = mapMarkers.length > 0
    ? mapMarkers.reduce((sum, m) => sum + m.lat, 0) / mapMarkers.length
    : 52.2297;
  const centerLng = mapMarkers.length > 0
    ? mapMarkers.reduce((sum, m) => sum + m.lng, 0) / mapMarkers.length
    : 21.0122;

  return (
    <>
      <Helmet>
        <title>{`${plate.toUpperCase()} - ${stats.totalSpots || 0} zgłoszeń | Motoradar24`}</title>
        <meta name="description" content={`Wszystkie zgłoszenia pojazdu ${plate.toUpperCase()}. ${stats.brand || 'Pojazd'} - ${stats.totalSpots || 0} przypadków chamstwa drogowego w ${stats.cities.join(', ')}.`} />
        <meta name="keywords" content={`${plate.toUpperCase()}, ${stats.brand || ''}, chamstwo drogowe, złe parkowanie`} />
        <link rel="canonical" href={`https://motoradar24.pl/tablica/${plate.toLowerCase()}`} />
        <meta property="og:title" content={`${plate.toUpperCase()} - ${stats.totalSpots || 0} zgłoszeń chamstwa`} />
        <meta property="og:description" content={`Zobacz wszystkie zgłoszenia pojazdu ${plate.toUpperCase()}`} />
        <meta property="og:image" content={spots[0]?.images?.[0] || '/og-image.jpg'} />
        <meta property="og:url" content={`https://motoradar24.pl/tablica/${plate.toLowerCase()}`} />
      </Helmet>

      {/* Modal raportu */}
      {showReport && (
        <ReportModal
          plate={plate}
          stats={stats}
          spots={spots}
          onClose={() => setShowReport(false)}
        />
      )}

      <div className="max-w-5xl mx-auto p-4 pb-20">
        {/* Header */}
        <div className="mb-4">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 text-sm hover:text-blue-600 transition-colors">
            <ArrowLeft size={18}/> Powrót
          </button>
        </div>

        {/* Plate Header */}
        <div className="bg-white rounded-2xl border p-6 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-black text-gray-900 mb-2">
                📋 {plate.toUpperCase()}
              </h1>
              {stats.brand && (
                <p className="text-lg text-gray-600">{stats.brand}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-3">
              <div className="text-right">
                <div className="text-3xl font-black text-red-600">{stats.totalSpots}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wider">Zgłoszeń</div>
              </div>
              {/* PRZYCISK RAPORTU */}
              <button
                onClick={() => setShowReport(true)}
                className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition-all shadow-lg active:scale-95"
              >
                <FileText size={16}/>
                Raport Pojazdu
              </button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <TrendingUp size={16} />
                <span className="text-xs font-bold uppercase">Głosy</span>
              </div>
              <div className="text-2xl font-black text-gray-900">{stats.totalUpvotes}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <MessageSquare size={16} />
                <span className="text-xs font-bold uppercase">Komentarze</span>
              </div>
              <div className="text-2xl font-black text-gray-900">{stats.totalComments}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <Calendar size={16} />
                <span className="text-xs font-bold uppercase">Pierwsze</span>
              </div>
              <div className="text-sm font-bold text-gray-900">
                {stats.firstSeen ? getRelativeTime(stats.firstSeen) : '-'}
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <Calendar size={16} />
                <span className="text-xs font-bold uppercase">Ostatnie</span>
              </div>
              <div className="text-sm font-bold text-gray-900">
                {stats.lastSeen ? getRelativeTime(stats.lastSeen) : '-'}
              </div>
            </div>
          </div>

          {/* Cities */}
          {stats.cities.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <MapPin size={16} />
                <span className="text-xs font-bold uppercase">Widziane w:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {stats.cities.map((city, i) => (
                  <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-bold">
                    {city}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Map */}
        {mapMarkers.length > 0 && (
          <div className="bg-white rounded-2xl border p-4 mb-4 shadow-sm">
            <h3 className="font-bold mb-4 flex items-center gap-2 text-sm text-gray-800">
              <MapPin size={18} className="text-blue-600"/> Wszystkie lokalizacje
            </h3>
            <div className="h-96 rounded-xl overflow-hidden">
              <MapContainer center={[centerLat, centerLng]} zoom={6} className="h-full w-full">
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
                {mapMarkers.map((marker, i) => (
                  <Marker key={i} position={[marker.lat, marker.lng]}>
                    <Popup>
                      <div className="text-xs">
                        <strong>{marker.label}</strong><br/>
                        {marker.createdAt && getRelativeTime(marker.createdAt)}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        )}

        {/* Timeline of Spots */}
        <div className="bg-white rounded-2xl border p-4 shadow-sm">
          <h3 className="font-bold mb-6 flex items-center gap-2 text-sm text-gray-800">
            <Calendar size={18} className="text-blue-600"/> Wszystkie zgłoszenia ({spots.length})
          </h3>
          <div className="space-y-4">
            {spots.map((spot) => (
              <Link
                key={spot.id}
                to={`/pojazd/${spot.plateSlug || spot.id}`}
                className="block bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors border border-gray-100 hover:border-blue-200"
              >
                <div className="flex gap-4">
                  {spot.images?.[0] && (
                    <img src={spot.images[0]} alt="spot" className="w-24 h-24 object-cover rounded-lg flex-shrink-0"/>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">{spot.brand}</h4>
                        <p className="text-xs text-gray-600 line-clamp-2">{spot.description}</p>
                      </div>
                      {spot.flair && spot.flair !== 'chamstwo' && (
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-bold ml-2 flex-shrink-0">
                          {spot.flair}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                      <span className="flex items-center gap-1"><Calendar size={12}/>{getRelativeTime(spot.createdAt)}</span>
                      <span className="flex items-center gap-1"><MapPin size={12}/>{spot.locationName || spot.city || 'Nieznana'}</span>
                      <span className="flex items-center gap-1"><TrendingUp size={12}/>{spot.upvotes || 0}</span>
                      <span className="flex items-center gap-1"><MessageSquare size={12}/>{spot.commentCount || 0}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-6 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white text-center">
          <h3 className="text-xl font-black mb-2">Widziałeś ten pojazd?</h3>
          <p className="text-blue-100 mb-4">Dodaj swoje zgłoszenie i pomóż innym!</p>
          <button
            onClick={() => navigate('/add')}
            className="bg-white text-blue-600 px-6 py-3 rounded-xl font-black text-sm uppercase tracking-wider hover:bg-blue-50 transition-colors"
          >
            Zgłoś teraz
          </button>
        </div>
      </div>
    </>
  );
};